import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SiretLookup from './SiretLookup';
import './FichierPro.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const categories = [
  'Restaurant', 'Pizzeria', 'Kebab', 'Traiteur',
  'Cafe', 'Bar',
  'Boulangerie', 'Patisserie', 'Chocolatier', 'Fromagerie', 'Poissonnier',
  'Salon de coiffure', 'Institut de beaute',
  'Epicerie fine', 'Supermarche', 'Boucherie', 'Fleuriste',
  'Pharmacie', 'Opticien', 'Dentiste', 'Medecin', 'Veterinaire',
  'Boutique vetements', 'Bijouterie',
  'Agence immobiliere', 'Banque', 'Assurance',
  'Garage auto', 'Caviste', 'Pressing', 'Librairie',
  'Tabac Presse', 'Hotel',
  'Magasin informatique', 'Plombier Electricien',
  'Serrurier', 'Peintre', 'Photographe', 'Tatoueur',
  'Auto-ecole', 'Salle de sport'
];

const calculateEndTime = (startTime) => {
  const [hours, minutes] = startTime.split(':').map(Number);
  const endHours = hours + 1;
  return `${endHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

// Jours FR et OSM
const joursFR = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const joursOSM = { 'Mo': 1, 'Tu': 2, 'We': 3, 'Th': 4, 'Fr': 5, 'Sa': 6, 'Su': 0 };

function isOpenNow(horaires) {
  if (!horaires || horaires.trim() === '') return null; // inconnu

  const now = new Date();
  const jourActuel = now.getDay(); // 0=dimanche
  const heureActuelle = now.getHours() * 60 + now.getMinutes(); // minutes depuis minuit
  const jourNomFR = joursFR[jourActuel].toLowerCase();

  // Format Google: "lundi: 09:30–19:00 | mardi: ..."
  if (horaires.includes('|') && horaires.toLowerCase().includes(jourNomFR)) {
    const parts = horaires.split('|').map(s => s.trim());
    for (const part of parts) {
      const lower = part.toLowerCase();
      if (!lower.startsWith(jourNomFR)) continue;
      // Fermé ?
      if (lower.includes('ferm')) return false;
      // Extraire les heures (format: "09:30–19:00" ou "09:30 – 19:00" avec tiret unicode)
      const times = part.match(/(\d{1,2}:\d{2})[^\d]*(\d{1,2}:\d{2})/);
      if (times) {
        const [, debut, fin] = times;
        const [dh, dm] = debut.split(':').map(Number);
        const [fh, fm] = fin.split(':').map(Number);
        const open = dh * 60 + dm;
        const close = fh * 60 + fm;
        if (heureActuelle >= open && heureActuelle < close) return true;
        return false;
      }
      return null;
    }
    return null;
  }

  // Format OSM: "Tu-Sa 10:00-18:00; Su,Mo off"
  if (horaires.match(/[A-Z][a-z]/)) {
    const segments = horaires.split(';').map(s => s.trim());
    let matchedOpen = null;

    for (const seg of segments) {
      // Vérifier si ce jour est inclus dans le segment
      const isOff = seg.toLowerCase().includes('off');

      // Parser les jours du segment
      const daysPart = seg.split(/\s+/)[0];
      const days = [];

      daysPart.split(',').forEach(part => {
        if (part.includes('-')) {
          const [start, end] = part.split('-');
          const si = joursOSM[start];
          const ei = joursOSM[end];
          if (si !== undefined && ei !== undefined) {
            let d = si;
            while (true) {
              days.push(d);
              if (d === ei) break;
              d = (d + 1) % 7;
            }
          }
        } else if (joursOSM[part] !== undefined) {
          days.push(joursOSM[part]);
        }
      });

      if (!days.includes(jourActuel)) continue;

      if (isOff) return false;

      // Extraire les heures
      const times = seg.match(/(\d{1,2}:\d{2})-(\d{1,2}:\d{2})/);
      if (times) {
        const [, debut, fin] = times;
        const [dh, dm] = debut.split(':').map(Number);
        const [fh, fm] = fin.split(':').map(Number);
        const open = dh * 60 + dm;
        const close = fh * 60 + fm;
        if (heureActuelle >= open && heureActuelle < close) matchedOpen = true;
        else matchedOpen = false;
      }
    }

    return matchedOpen;
  }

  return null; // format non reconnu
}

const statutLabels = {
  nouveau: 'Nouveau',
  a_rappeler: 'A rappeler',
  interesse: 'Intéressé',
  pas_interesse: 'Pas intéressé',
  rdv_pris: 'RDV pris',
  ne_pas_rappeler: 'Ne pas rappeler',
  nrp: 'NRP'
};

function EShopsProsper({ currentUser }) {
  const [activite, setActivite] = useState('');
  const [ville, setVille] = useState('');
  const [searchText, setSearchText] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchDone, setSearchDone] = useState(false);
  const [dbStats, setDbStats] = useState({ total: 0, byActivite: [], byVille: [] });
  const [detailFiche, setDetailFiche] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [filterOuvert, setFilterOuvert] = useState(''); // '', 'ouvert', 'ferme'

  // RDV Modal state
  const [showModal, setShowModal] = useState(false);
  const [commerciaux, setCommerciaux] = useState([]);
  const [rdvCreated, setRdvCreated] = useState({});
  const [formData, setFormData] = useState({
    commercial_id: '',
    titre: '',
    description: '',
    client_nom: '',
    client_telephone: '',
    client_adresse: '',
    date_rdv: '',
    heure_debut: '09:00',
    heure_fin: '10:00',
    statut: 'planifie'
  });

  const ITEMS_PER_PAGE = 50;

  useEffect(() => {
    const fetchCommerciaux = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/planning/commerciaux`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCommerciaux(response.data);
      } catch (err) {
        console.error('Erreur chargement commerciaux:', err);
      }
    };
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/fichier-pro/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDbStats(prev => ({
          ...prev,
          total: response.data.total,
          byVille: response.data.byVille || [],
          byActivite: response.data.byActivite || []
        }));
      } catch (err) {
        console.error('Erreur chargement stats:', err);
      }
    };
    fetchCommerciaux();
    fetchStats();
  }, []);

  const handleStatutChange = async (telephoneClean, newStatut) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/fichier-pro/statut`, {
        telephone_clean: telephoneClean,
        statut: newStatut
      }, { headers: { Authorization: `Bearer ${token}` } });
      const isExclusif = ['a_rappeler', 'interesse', 'pas_interesse', 'rdv_pris', 'ne_pas_rappeler', 'nrp'].includes(newStatut);
      setResults(prev => prev.map(r =>
        r.telephone_clean === telephoneClean ? {
          ...r,
          statut: newStatut,
          statut_par: isExclusif ? currentUser.id : null,
          statut_par_nom: isExclusif ? currentUser.nom : null,
          statut_date: isExclusif ? new Date().toISOString() : null
        } : r
      ));
    } catch (err) {
      console.error('Erreur changement statut:', err);
      if (err.response && err.response.status === 403) {
        alert(err.response.data.error || 'Cette fiche est assignée à un autre utilisateur');
      }
    }
  };

  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (activite) params.append('activite', activite);
      if (ville) params.append('ville', ville);
      if (searchText) params.append('search', searchText);
      if (filterStatut) params.append('statut', filterStatut);

      const response = await axios.get(`${API_URL}/api/fichier-pro/export-csv?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 120000
      });

      // Téléchargement du fichier
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `eshops-base-${today}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de l\'export CSV');
    } finally {
      setExportLoading(false);
    }
  };

  const handleSearchBase = async (pageNum) => {
    const targetPage = typeof pageNum === 'number' ? pageNum : 1;
    setLoading(true);
    setError('');
    if (targetPage === 1) setResults([]);
    setSearchDone(false);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/fichier-pro/base`, {
        activite: activite || undefined,
        ville: ville || undefined,
        search: searchText || undefined,
        statut: filterStatut || undefined,
        page: targetPage,
        limit: ITEMS_PER_PAGE
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000
      });

      setResults(response.data.results || []);
      setTotalResults(response.data.total || 0);
      setCurrentPage(targetPage);
      setTotalPages(response.data.totalPages || 1);
      setDbStats(response.data.stats || { total: 0, byActivite: [], byVille: [] });
      setSearchDone(true);
    } catch (err) {
      const msg = err.response?.data?.error || 'Erreur lors de la consultation de la base.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRdvModal = (result) => {
    setFormData({
      commercial_id: commerciaux[0]?.id || '',
      titre: `RDV ${activite || result.activite} - ${result.nom}`,
      description: `Source: E-Shops Prosper${result.site_web ? `\nSite web: ${result.site_web}` : ''}${result.horaires ? `\nHoraires: ${result.horaires}` : ''}`,
      client_nom: result.nom,
      client_telephone: result.telephone,
      client_adresse: result.adresse || '',
      date_rdv: new Date().toISOString().split('T')[0],
      heure_debut: '09:00',
      heure_fin: '10:00',
      statut: 'planifie'
    });
    setShowModal(true);
  };

  const handleSubmitRdv = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const dataToSend = {
        ...formData,
        heure_fin: calculateEndTime(formData.heure_debut)
      };
      await axios.post(`${API_URL}/api/planning/rdv`, dataToSend, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowModal(false);
      const currentResult = results.find(r =>
        r.nom === formData.client_nom && r.telephone === formData.client_telephone
      );
      if (currentResult) {
        setRdvCreated(prev => ({ ...prev, [currentResult.id || currentResult.telephone_clean]: true }));
      }
    } catch (err) {
      console.error('Erreur creation RDV:', err);
      alert(err.response?.data?.error || 'Erreur lors de la creation du RDV');
    }
  };

  const getSourceBadge = (source) => {
    if (source === 'google') return <span className="source-badge source-google">Google</span>;
    if (source === 'osm+google') return <span className="source-badge source-both">OSM+Google</span>;
    return <span className="source-badge source-osm">OSM</span>;
  };

  // Villes extraites des stats
  const villesFromStats = dbStats.byVille ? dbStats.byVille.map(v => v.ville) : [];

  // Filtre ouvert/fermé côté frontend (pas d'appel API)
  const filteredResults = filterOuvert
    ? results.filter(r => {
        const status = isOpenNow(r.horaires);
        if (filterOuvert === 'ouvert') return status === true;
        if (filterOuvert === 'ferme') return status === false;
        return true;
      })
    : results;

  return (
    <div className="fichier-pro">
      <div className="fichier-pro-header">
        <h2>E-Shops Base</h2>
        <p className="fichier-pro-subtitle">
          Base de commerçants | <strong>{dbStats.total}</strong> fiches enregistrées
        </p>
      </div>

      {/* Filtres */}
      <div className="fichier-pro-filters">
        <div className="filter-group">
          <label>Activité (optionnel)</label>
          <select value={activite} onChange={e => setActivite(e.target.value)}>
            <option value="">-- Toutes --</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Ville (optionnel)</label>
          <select value={ville} onChange={e => setVille(e.target.value)}>
            <option value="">-- Toutes --</option>
            {villesFromStats.map(v => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Recherche libre</label>
          <input
            type="text"
            className="filter-input"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            placeholder="Nom, adresse, téléphone..."
            onKeyDown={e => e.key === 'Enter' && handleSearchBase()}
          />
        </div>

        <div className="filter-group">
          <label>Statut</label>
          <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)}>
            <option value="">-- Tous --</option>
            {Object.entries(statutLabels).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Ouverture</label>
          <select value={filterOuvert} onChange={e => setFilterOuvert(e.target.value)}>
            <option value="">-- Tous --</option>
            <option value="ouvert">Ouvert maintenant</option>
            <option value="ferme">Fermé maintenant</option>
          </select>
        </div>

        <button
          className="btn-search"
          onClick={() => handleSearchBase()}
          disabled={loading}
        >
          {loading ? (
            <><span className="spinner-small"></span> Recherche...</>
          ) : (
            'Consulter'
          )}
        </button>

        {currentUser?.role === 'admin' && (
          <button
            className="btn-search"
            style={{ background: '#2e7d32', marginLeft: '8px' }}
            onClick={handleExportCSV}
            disabled={exportLoading}
            title="Exporter en CSV (avec les filtres actifs)"
          >
            {exportLoading ? (
              <><span className="spinner-small"></span> Export...</>
            ) : (
              'Exporter CSV'
            )}
          </button>
        )}
      </div>

      {/* Erreur */}
      {error && (
        <div className="fichier-pro-error">
          {error}
        </div>
      )}

      {/* Résultats */}
      {searchDone && !loading && (
        <div className="fichier-pro-results">
          <div className="results-header">
            <span className="results-count">
              {filterOuvert
                ? <>{filteredResults.length} / {totalResults} résultat{totalResults !== 1 ? 's' : ''} ({filterOuvert === 'ouvert' ? 'ouverts' : 'fermés'})</>
                : <>{totalResults} résultat{totalResults !== 1 ? 's' : ''} trouvé{totalResults !== 1 ? 's' : ''}</>
              }
            </span>
            <div className="results-meta">
              {totalPages > 1 && (
                <span className="results-page">
                  Page {currentPage} / {totalPages}
                </span>
              )}
            </div>
          </div>

          {filteredResults.length > 0 ? (
            <>
              <div className="table-wrapper">
                <table className="fichier-pro-table">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Téléphone</th>
                      <th>Statut</th>
                      <th>Statué par</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredResults.map((r, idx) => (
                      <tr key={r.id || r.telephone_clean || idx}>
                        <td className="td-nom">{r.nom}</td>
                        <td className="td-phone">
                          <a href={`tel:${r.telephone}`} className="phone-link">
                            {r.telephone}
                          </a>
                        </td>
                        <td className="td-statut">
                          <select
                            className={`statut-select statut-${r.statut || 'nouveau'}`}
                            value={r.statut || 'nouveau'}
                            onChange={e => handleStatutChange(r.telephone_clean, e.target.value)}
                          >
                            {Object.entries(statutLabels).map(([key, label]) => (
                              <option key={key} value={key}>{label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="td-statut-par">
                          {r.statut_par_nom && (
                            <span className="statut-par-info">
                              {r.statut_par_nom}
                              <br/>
                              <small>{new Date(r.statut_date).toLocaleDateString('fr-FR')} {new Date(r.statut_date).toLocaleTimeString('fr-FR', {hour:'2-digit', minute:'2-digit'})}</small>
                            </span>
                          )}
                        </td>
                        <td className="td-actions">
                          <button className="btn-voir-fiche" onClick={() => setDetailFiche(r)}>Voir</button>
                          {rdvCreated[r.id || r.telephone_clean] ? (
                            <span className="badge-added">RDV</span>
                          ) : (
                            <button
                              className="btn-add-rdv"
                              onClick={() => handleOpenRdvModal(r)}
                            >
                              RDV
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Modal détail fiche */}
              {detailFiche && (
                <div className="modal-overlay">
                  <div className="modal-content planning-modal" onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                      <h3>{detailFiche.nom}</h3>
                      <button className="btn-close" onClick={() => setDetailFiche(null)}>&times;</button>
                    </div>
                    <div className="fiche-detail-body">
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Activité</span>
                        <span className="fiche-value">{detailFiche.activite || '-'}</span>
                      </div>
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Téléphone</span>
                        <span className="fiche-value">
                          <a href={`tel:${detailFiche.telephone}`} className="phone-link">{detailFiche.telephone}</a>
                        </span>
                      </div>
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Adresse</span>
                        <span className="fiche-value">{detailFiche.adresse || '-'}</span>
                      </div>
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Ville</span>
                        <span className="fiche-value">{detailFiche.ville || '-'}</span>
                      </div>
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Code postal</span>
                        <span className="fiche-value">{detailFiche.code_postal || '-'}</span>
                      </div>
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Site web</span>
                        <span className="fiche-value">
                          {detailFiche.site_web ? (
                            <a href={detailFiche.site_web.startsWith('http') ? detailFiche.site_web : `https://${detailFiche.site_web}`} target="_blank" rel="noopener noreferrer" className="web-link">{detailFiche.site_web}</a>
                          ) : '-'}
                        </span>
                      </div>
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Horaires</span>
                        <span className="fiche-value fiche-horaires">{detailFiche.horaires || '-'}</span>
                      </div>
                      {currentUser && currentUser.role === 'admin' && (
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Source</span>
                        <span className="fiche-value">{getSourceBadge(detailFiche.source)}</span>
                      </div>
                      )}
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Statut</span>
                        <span className="fiche-value">
                          <select
                            className={`statut-select statut-${detailFiche.statut || 'nouveau'}`}
                            value={detailFiche.statut || 'nouveau'}
                            onChange={e => {
                              handleStatutChange(detailFiche.telephone_clean, e.target.value);
                              setDetailFiche(prev => ({ ...prev, statut: e.target.value }));
                            }}
                          >
                            {Object.entries(statutLabels).map(([key, label]) => (
                              <option key={key} value={key}>{label}</option>
                            ))}
                          </select>
                        </span>
                      </div>
                      {detailFiche.statut_par_nom && (
                        <div className="fiche-detail-row">
                          <span className="fiche-label">Statué par</span>
                          <span className="fiche-value">
                            {detailFiche.statut_par_nom} - {new Date(detailFiche.statut_date).toLocaleDateString('fr-FR')} {new Date(detailFiche.statut_date).toLocaleTimeString('fr-FR', {hour:'2-digit', minute:'2-digit'})}
                          </span>
                        </div>
                      )}
                      <div className="fiche-detail-actions">
                        {!rdvCreated[detailFiche.id || detailFiche.telephone_clean] && (
                          <button className="btn-add-rdv" onClick={() => { handleOpenRdvModal(detailFiche); setDetailFiche(null); }}>
                            Prendre RDV
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => handleSearchBase(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    Précédent
                  </button>
                  {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                    let page;
                    if (totalPages <= 10) {
                      page = i + 1;
                    } else if (currentPage <= 5) {
                      page = i + 1;
                    } else if (currentPage >= totalPages - 4) {
                      page = totalPages - 9 + i;
                    } else {
                      page = currentPage - 4 + i;
                    }
                    return (
                      <button
                        key={page}
                        className={currentPage === page ? 'active' : ''}
                        onClick={() => handleSearchBase(page)}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => handleSearchBase(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Suivant
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="no-results">
              Aucun commerçant trouvé dans la base. Lancez des recherches dans Recherche Pro pour alimenter la base.
            </div>
          )}
        </div>
      )}

      {/* Stats */}
      {searchDone && dbStats.byActivite && dbStats.byActivite.length > 0 && (
        <div className="fichier-pro-db-stats">
          <div className="db-stats-section">
            <h4>Par activité</h4>
            <div className="db-stats-tags">
              {dbStats.byActivite.map(s => (
                <span key={s.activite} className="db-stat-tag" onClick={() => { setActivite(s.activite); }}>
                  {s.activite} ({s.count})
                </span>
              ))}
            </div>
          </div>
          {dbStats.byVille && dbStats.byVille.length > 0 && (
            <div className="db-stats-section">
              <h4>Par ville</h4>
              <div className="db-stats-tags">
                {dbStats.byVille.map(s => (
                  <span key={s.ville} className="db-stat-tag" onClick={() => { setVille(s.ville); }}>
                    {s.ville} ({s.count})
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal Nouveau RDV */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content planning-modal">
            <div className="modal-header">
              <h3>Nouveau Rendez-vous</h3>
              <button className="btn-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmitRdv}>
              <SiretLookup
                compact
                onCompanyFound={(data) => {
                  setFormData(prev => ({
                    ...prev,
                    client_nom: data.dirigeant_nom || prev.client_nom,
                    client_adresse: data.adresse || prev.client_adresse,
                    titre: prev.titre || `RDV ${data.entreprise}`,
                    description: prev.description || `Entreprise: ${data.entreprise}${data.dirigeant_qualite ? `\nDirigeant: ${data.dirigeant_nom} (${data.dirigeant_qualite})` : ''}`
                  }));
                }}
              />
              <div className="form-row">
                <div className="form-group">
                  <label>Commercial *</label>
                  <select
                    value={formData.commercial_id}
                    onChange={e => setFormData({...formData, commercial_id: parseInt(e.target.value)})}
                    required
                  >
                    {commerciaux.map(c => (
                      <option key={c.id} value={c.id}>{c.nom}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Statut</label>
                  <select
                    value={formData.statut}
                    onChange={e => setFormData({...formData, statut: e.target.value})}
                  >
                    <option value="planifie">Planifié</option>
                    {(currentUser?.role === 'admin' || currentUser?.id === 2) && (
                      <option value="confirme">Confirmé</option>
                    )}
                    <option value="annule">Annulé</option>
                    <option value="termine">Terminé</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Titre du RDV *</label>
                <input
                  type="text"
                  value={formData.titre}
                  onChange={e => setFormData({...formData, titre: e.target.value})}
                  placeholder="Ex: Presentation produit, Signature contrat..."
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    value={formData.date_rdv}
                    onChange={e => setFormData({...formData, date_rdv: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Heure *</label>
                  <input
                    type="time"
                    value={formData.heure_debut}
                    onChange={e => setFormData({...formData, heure_debut: e.target.value})}
                    required
                  />
                  <small className="form-hint">Durée: 1h</small>
                </div>
              </div>

              <div className="form-group">
                <label>Nom du client</label>
                <input
                  type="text"
                  value={formData.client_nom}
                  onChange={e => setFormData({...formData, client_nom: e.target.value})}
                  placeholder="Nom du client ou prospect"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Téléphone client</label>
                  <input
                    type="tel"
                    value={formData.client_telephone}
                    onChange={e => setFormData({...formData, client_telephone: e.target.value})}
                    placeholder="06 XX XX XX XX"
                  />
                </div>
                <div className="form-group">
                  <label>Adresse</label>
                  <input
                    type="text"
                    value={formData.client_adresse}
                    onChange={e => setFormData({...formData, client_adresse: e.target.value})}
                    placeholder="Lieu du rendez-vous"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description / Notes</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({...formData, description: e.target.value})}
                  rows="3"
                  placeholder="Détails du rendez-vous..."
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-submit">
                  Créer le RDV
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default EShopsProsper;
